import { AxiosError } from 'axios';
import React, { useState } from 'react';
import { Alert, Badge, Button, Form, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { toastConfig } from 'utils/toastConfig';
import {
  OperatingExpensePreview,
  OperatingExpensePreviewRow,
  formatPesos
} from './expenseOptions';

type UploadExpensesFormProps = {
  branchDomain: string;
  onSaved: () => void;
};

const apiError = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { errorMessage?: string } | undefined;
    return data?.errorMessage || fallback;
  }
  return fallback;
};

const amountLabel = (row: OperatingExpensePreviewRow) => {
  if (row.amount !== null && row.amount !== undefined) return formatPesos(row.amount);
  return row.amountText || '—';
};

const UploadExpensesForm = ({ branchDomain, onSaved }: UploadExpensesFormProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<OperatingExpensePreview | null>(null);
  const [isWorking, setIsWorking] = useState(false);

  const downloadTemplate = async () => {
    try {
      const response = await axiosInstance.get<Blob>(
        `/api/v1/${branchDomain}/operating-expenses/template`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'operating-expenses-template.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download the template.', {
        ...toastConfig,
        autoClose: 3000
      });
    }
  };

  const previewFile = async () => {
    if (!file) {
      toast.error('Choose a spreadsheet first.', { ...toastConfig, autoClose: 3000 });
      return;
    }

    const formData = new FormData();
    formData.append('formFile', file);
    try {
      setIsWorking(true);
      const { data } = await axiosInstance.post<OperatingExpensePreview>(
        `/api/v1/${branchDomain}/operating-expenses/preview`,
        formData
      );
      setPreview(data);
    } catch (error) {
      toast.error(apiError(error, 'Could not read the spreadsheet.'), {
        ...toastConfig,
        autoClose: 3000
      });
    } finally {
      setIsWorking(false);
    }
  };

  const saveReadyRows = async () => {
    if (!preview || preview.readyCount === 0) return;

    const rows = preview.rows
      .filter(row => row.status === 'Ready')
      .map(row => ({
        expenseDate: row.expenseDate,
        workplace: row.workplace,
        category: row.category,
        payee: row.payee,
        description: row.description,
        amount: row.amount,
        paymentMethod: row.paymentMethod,
        fundId: row.fundId ?? null,
        reference: row.reference || null
      }));

    try {
      setIsWorking(true);
      const { data } = await axiosInstance.post<{
        savedCount: number;
        skippedCount: number;
      }>(`/api/v1/${branchDomain}/operating-expenses/import`, {
        fileName: preview.fileName || file?.name,
        skippedCount: preview.errorCount,
        rows
      });
      toast.success(
        `Saved ${data.savedCount} expense${data.savedCount === 1 ? '' : 's'}. ${data.skippedCount} skipped.`,
        { ...toastConfig, autoClose: 3000 }
      );
      onSaved();
    } catch (error) {
      toast.error(apiError(error, 'Could not save the expenses.'), {
        ...toastConfig,
        autoClose: 3000
      });
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <div className="fw-semibold">Spreadsheet</div>
          <div className="text-body-tertiary fs-9">
            Date, Store or Shop, Category, Payee, Description, Amount, Payment
            method, Reference
          </div>
        </div>
        <Button variant="link" className="px-0" onClick={downloadTemplate}>
          Download template
        </Button>
      </div>
      <Form.Group className="mb-3">
        <Form.Control
          type="file"
          accept=".xlsx,.csv"
          onChange={event => {
            const selected = (event.target as HTMLInputElement).files?.[0] ?? null;
            setFile(selected);
            setPreview(null);
          }}
        />
      </Form.Group>
      <Button
        variant="phoenix-secondary"
        className="mb-3"
        disabled={isWorking}
        onClick={previewFile}
      >
        Preview
      </Button>
      {preview && (
        <>
          <Alert variant={preview.errorCount > 0 ? 'warning' : 'success'}>
            {preview.readyCount} ready, {preview.errorCount} with errors. Only
            ready rows are saved.
          </Alert>
          <div style={{ maxHeight: 360, overflow: 'auto' }}>
            <Table size="sm" responsive>
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Store or shop</th>
                  <th>Category</th>
                  <th>Payee</th>
                  <th>Amount</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.length === 0 && (
                  <tr>
                    <td colSpan={8}>The file has no expense rows.</td>
                  </tr>
                )}
                {preview.rows.map(row => (
                  <tr key={row.rowNumber}>
                    <td>{row.rowNumber}</td>
                    <td>
                      <Badge bg={row.status === 'Ready' ? 'success' : 'danger'}>
                        {row.status}
                      </Badge>
                    </td>
                    <td>{row.expenseDate || '—'}</td>
                    <td>{row.workplaceLabel || row.workplace || '—'}</td>
                    <td>{row.categoryLabel || row.category || '—'}</td>
                    <td>{row.payee || '—'}</td>
                    <td>{amountLabel(row)}</td>
                    <td>{row.message || ''}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {preview.readyCount > 0 && (
            <Button
              variant="primary"
              className="mt-3"
              disabled={isWorking}
              onClick={saveReadyRows}
            >
              Save ready rows
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default UploadExpensesForm;
